import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Play,
  ShoppingBag,
  Sparkles,
  Star,
  Tag,
} from "lucide-react";
import hero from "@/assets/hero-products.jpg";
import type { RecentVideo } from "@/data/products";

type HeroStats = {
  offersLabel: string;
  storesLabel: string;
  discountLabel: string;
};

type HeroProps = {
  onSeeOffers: () => void;
  stats: HeroStats;
  recentVideos: RecentVideo[];
};

// Fallback: se o vídeo não disparar onEnded em N segundos, avança mesmo assim
const FALLBACK_ADVANCE_MS = 30_000;

// Faixa de legendas vazia: os vídeos não têm narração, mas o elemento <track>
// é exigido por auditorias de acessibilidade em <video>.
const EMPTY_CAPTIONS = "data:text/vtt;charset=utf-8,WEBVTT%0A%0A";

/**
 * Decide se vale a pena baixar o vídeo do showcase automaticamente.
 *
 * Cada vídeo tem ~15 MB. Em telas pequenas / conexões lentas isso destrói o LCP
 * (medido: 12,8 s no PageSpeed mobile), então só liberamos o autoplay em telas
 * grandes e conexões boas. No mobile o usuário toca para assistir.
 */
function shouldAutoloadVideo(): boolean {
  if (typeof window === "undefined") return false;
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (connection?.saveData) return false;
  if (connection?.effectiveType && /^(slow-)?2g$/.test(connection.effectiveType)) return false;
  return window.matchMedia("(min-width: 1024px)").matches;
}

export function Hero({ onSeeOffers, stats, recentVideos }: HeroProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  // Começa sempre falso: nada de vídeo no caminho crítico da primeira pintura.
  const [videoEnabled, setVideoEnabled] = useState(false);
  const hasRecentVideos = recentVideos.length > 0;

  // Libera o vídeo só depois do load, e apenas em tela grande / conexão boa.
  useEffect(() => {
    if (!hasRecentVideos || videoEnabled) return;
    let cancelled = false;
    const enable = () => {
      if (!cancelled && shouldAutoloadVideo()) setVideoEnabled(true);
    };
    const schedule = () => {
      const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback;
      if (idle) idle(enable);
      else window.setTimeout(enable, 1500);
    };
    if (document.readyState === "complete") {
      schedule();
      return () => {
        cancelled = true;
      };
    }
    window.addEventListener("load", schedule, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
    };
  }, [hasRecentVideos, videoEnabled]);

  const playVideo = useCallback(() => {
    setVideoEnabled(true);
  }, []);

  // Fallback timer caso onEnded não dispare (ex: vídeo muito longo ou loop)
  useEffect(() => {
    if (!hasRecentVideos) return;
    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % recentVideos.length);
    }, FALLBACK_ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [activeIndex, hasRecentVideos, recentVideos.length]);

  useEffect(() => {
    if (!recentVideos.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex >= recentVideos.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, recentVideos.length]);

  const handleVideoEnded = () => {
    setActiveIndex((current) => (current + 1) % recentVideos.length);
  };

  const activeVideo = recentVideos[activeIndex] ?? null;

  const priceLabel = useMemo(() => {
    if (!activeVideo?.currentPrice) return null;
    return formatCurrency(activeVideo.currentPrice);
  }, [activeVideo]);

  const originalPriceLabel = useMemo(() => {
    if (
      !activeVideo?.originalPrice ||
      !activeVideo.currentPrice ||
      activeVideo.originalPrice <= activeVideo.currentPrice
    ) {
      return null;
    }
    return formatCurrency(activeVideo.originalPrice);
  }, [activeVideo]);

  const goToPrevious = () => {
    setActiveIndex((current) => {
      if (!recentVideos.length) {
        return 0;
      }
      return current === 0 ? recentVideos.length - 1 : current - 1;
    });
  };

  const goToNext = () => {
    setActiveIndex((current) => {
      if (!recentVideos.length) {
        return 0;
      }
      return (current + 1) % recentVideos.length;
    });
  };

  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ background: "var(--gradient-hero)" }}
      />
      <div className="absolute -left-24 top-10 -z-10 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute -right-20 bottom-0 -z-10 h-80 w-80 rounded-full bg-accent/40 blur-3xl" />

      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[1.05fr_1fr] lg:py-24">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/70 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> Curadoria diária de promoções
          </span>
          <h1 className="mt-5 font-display text-5xl leading-[1.02] tracking-tight md:text-6xl lg:text-[5.25rem]">
            Os melhores <em className="not-italic text-primary">achadinhos</em> do dia em um só
            lugar
          </h1>
          <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
            Encontre produtos incríveis, promoções e tendências selecionadas para você. Clique
            no produto e compre diretamente na loja parceira.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={onSeeOffers}
              className="rounded-full bg-[image:var(--gradient-primary)] px-7 shadow-[var(--shadow-soft)] hover:opacity-95"
            >
              Ver ofertas <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full border-foreground/15 bg-card/70 px-7 backdrop-blur hover:bg-card"
            >
              Categorias
            </Button>
          </div>
          <dl className="mt-10 grid max-w-md grid-cols-3 gap-4 text-sm">
            <Stat icon={<Tag className="h-4 w-4" />} k={stats.offersLabel} v="ofertas/dia" />
            <Stat icon={<ShoppingBag className="h-4 w-4" />} k={stats.storesLabel} v="lojas parceiras" />
            <Stat icon={<Sparkles className="h-4 w-4" />} k={stats.discountLabel} v="até de desconto" />
          </dl>
        </div>

        <div className="relative">
          {hasRecentVideos && activeVideo ? (
            <div className="relative">
              <a
                href={activeVideo.affiliateLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block aspect-[5/4] overflow-hidden rounded-[2rem] border border-card bg-card shadow-[var(--shadow-soft)]"
              >
                {videoEnabled ? (
                  <video
                    key={activeVideo.videoUrl}
                    src={activeVideo.videoUrl}
                    poster={activeVideo.thumbnailUrl || undefined}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    onEnded={handleVideoEnded}
                  >
                    <track kind="captions" srcLang="pt" label="Sem narração" src={EMPTY_CAPTIONS} default />
                  </video>
                ) : (
                  <VideoPoster thumbnailUrl={activeVideo.thumbnailUrl} title={activeVideo.title} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
                <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/92 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-[var(--shadow-card)] backdrop-blur md:left-4 md:top-4 md:px-3 md:py-1.5 md:text-xs">
                  <Play className="h-3 w-3 text-primary md:h-3.5 md:w-3.5" />
                  Vídeos publicados
                </div>
                {activeVideo.rating ? (
                  <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-card/95 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-[var(--shadow-card)] md:right-4 md:top-4 md:gap-1.5 md:px-3 md:py-1.5 md:text-xs">
                    <Star className="h-3 w-3 fill-current text-primary md:h-3.5 md:w-3.5" />
                    {activeVideo.rating.toFixed(1)} favorito
                  </div>
                ) : null}
                {/* Overlay inferior — compacto no mobile, completo no md+ */}
                <div className="absolute inset-x-0 bottom-0 p-2.5 md:p-5 lg:p-6">
                  <div className="rounded-xl border border-white/15 bg-black/50 p-2.5 text-white shadow-[var(--shadow-card)] backdrop-blur-md md:rounded-[1.5rem] md:p-4">
                    <p className="line-clamp-1 text-[11px] font-medium text-white/90 md:line-clamp-2 md:text-sm lg:text-base">
                      {activeVideo.title}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-end gap-2 md:mt-3 md:gap-3">
                      {priceLabel ? (
                        <span className="font-display text-lg leading-none text-white md:text-2xl lg:text-3xl">
                          {priceLabel}
                        </span>
                      ) : null}
                      {originalPriceLabel ? (
                        <span className="text-[11px] text-white/60 line-through md:text-sm lg:text-base">
                          {originalPriceLabel}
                        </span>
                      ) : null}
                      {activeVideo.discountPercent > 0 ? (
                        <span className="rounded-full bg-[image:var(--gradient-primary)] px-2 py-0.5 text-[10px] font-semibold text-primary-foreground md:px-3 md:py-1 md:text-xs">
                          -{activeVideo.discountPercent}% hoje
                        </span>
                      ) : null}
                    </div>
                    {/* Hint de clique — apenas md+ para não ocupar espaço no mobile */}
                    <p className="mt-2 hidden text-[10px] font-medium uppercase tracking-[0.18em] text-white/70 md:block">
                      Toque para abrir o produto
                    </p>
                  </div>
                </div>
              </a>

              {videoEnabled ? null : (
                <button
                  type="button"
                  onClick={playVideo}
                  aria-label={`Reproduzir vídeo de ${activeVideo.title}`}
                  className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-foreground shadow-[var(--shadow-soft)] transition hover:scale-105"
                >
                  <Play className="ml-0.5 h-6 w-6 fill-current" />
                </button>
              )}

              <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4">
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/35 text-white shadow-[var(--shadow-card)] backdrop-blur transition hover:bg-black/50"
                  aria-label="Vídeo anterior"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/35 bg-black/35 text-white shadow-[var(--shadow-card)] backdrop-blur transition hover:bg-black/50"
                  aria-label="Próximo vídeo"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative aspect-[5/4] overflow-hidden rounded-[2rem] border border-card bg-card shadow-[var(--shadow-soft)]">
              <img
                src={hero}
                alt="Composição de produtos femininos: bolsa, maquiagem, óculos e acessórios"
                className="h-full w-full object-cover"
                width={1280}
                height={960}
              />
            </div>
          )}

          {hasRecentVideos ? (
            <div className="mt-4 flex items-center justify-between gap-4">
              <div className="flex gap-2">
                {recentVideos.map((item, index) => (
                  <button
                    key={`${item.assetId}-${item.videoId}`}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="grid h-6 w-6 place-items-center"
                    aria-label={`Ir para vídeo ${index + 1}`}
                    aria-current={index === activeIndex}
                  >
                    <span
                      className={`h-2.5 rounded-full transition ${
                        index === activeIndex ? "w-6 bg-primary" : "w-2.5 bg-primary/25 hover:bg-primary/45"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <div className="rounded-full bg-card/80 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-[var(--shadow-card)] backdrop-blur">
                {activeIndex + 1}/{recentVideos.length}
              </div>
            </div>
          ) : (
            <FloatingTag className="-right-2 top-1/3 md:-right-6">4.9 favoritado</FloatingTag>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Cartão de estatística do hero.
 *
 * O <dt> tem de ser filho direto deste <div> agrupador (o <dl> pai só aceita
 * dt/dd/div), então o ícone entra dentro do próprio <dt> em vez de num flex
 * container extra.
 */
function Stat({ icon, k, v }: { icon: React.ReactNode; k: string; v: string }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card/70 p-3 backdrop-blur">
      <dt className="flex items-center gap-1.5 font-display text-lg leading-none text-primary">
        <span aria-hidden>{icon}</span>
        {k}
      </dt>
      <dd className="mt-1 text-xs text-muted-foreground">{v}</dd>
    </div>
  );
}

/**
 * Substituto leve do <video> enquanto ele não é liberado.
 *
 * Renderiza a thumbnail (quando o pipeline gerou uma) ou um fundo em degradê —
 * de qualquer forma sem tocar nos ~15 MB do mp4, que é o que arrastava o LCP.
 */
function VideoPoster({
  thumbnailUrl,
  title,
}: {
  thumbnailUrl?: string | null;
  title: string;
}) {
  return (
    <div className="absolute inset-0 h-full w-full">
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          width={640}
          height={512}
          fetchPriority="high"
          className="h-full w-full object-cover"
        />
      ) : (
        <div
          aria-hidden
          className="h-full w-full"
          style={{ background: "var(--gradient-primary)" }}
        />
      )}
    </div>
  );
}

function FloatingTag({
  children,
  className = "",
  pink = false,
}: {
  children: React.ReactNode;
  className?: string;
  pink?: boolean;
}) {
  return (
    <span
      className={`absolute rounded-full border border-card px-3 py-1.5 text-xs font-medium shadow-[var(--shadow-card)] ${
        pink ? "bg-[image:var(--gradient-primary)] text-primary-foreground" : "bg-card text-foreground"
      } ${className}`}
    >
      {children}
    </span>
  );
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}
